// src/components/tutor/tutorPortalRoutes.js
// URL routing map for the Tutor Portal desktop dashboard.
// Mirrors the Admin Portal approach (adminPortalRoutes.js) so each major
// sidebar page / subpage has its own clean URL, can be opened directly, and
// supports normal browser back/forward navigation.

export const TUTOR_DEFAULT_TAB = "announcements";

// Tab key → canonical browser path
const TAB_PATHS = {
  announcements:      "/announcements",
  suggestions:        "/suggestions",
  schedule:           "/schedule",
  session__log:       "/session/log",
  session__progress:  "/session/progress-report",
  curriculum:         "/curriculum",
  student__directory: "/students/directory",
  student__diagnostics: "/students/diagnostics",
  student__plans:     "/students/plans",
  availability:       "/availability",
  payroll:            "/payroll",
  myinfo__profile:    "/my-info/profile",
  myinfo__subjects:   "/my-info/subjects",
};

// Route patterns registered in App.js for the tutor role. The "/" route is
// declared separately in App.js. Grouped sections also register a "/*" splat
// so bare group paths and any future nested navigation still resolve to the
// dashboard rather than falling through to the catch-all redirect.
export const TUTOR_PORTAL_ROUTE_PATHS = [
  "/announcements",
  "/suggestions",
  "/schedule",
  "/availability",
  "/payroll",
  "/session",
  "/session/*",
  "/curriculum",
  "/curriculum/*",
  "/students",
  "/students/*",
  "/my-info",
  "/my-info/*",
];

// Paths whose query string must survive the canonical-path redirect. The
// Diagnostic Reports page reads ?studentId= to preselect a student, and the
// student profile's Plan tab reads ?plan= to open a specific plan.
const PRESERVE_SEARCH_PATHS = new Set(["/students/diagnostics"]);

// Student-detail paths keep their query string so ?plan=<id> deep links (Plans
// hub, session-log "View full plan") resolve to the plan's detail view.
const PRESERVE_SEARCH_PREFIXES = ["/students/directory/"];

// Path prefixes that carry a record identifier in the URL (e.g. the student
// detail page at /students/directory/<studentId>). getCanonicalTutorPath
// returns these verbatim so the normalize effect never strips the id back to
// the bare directory path.
const PRESERVED_PATH_PREFIXES = ["/students/directory/"];

function normalizePathname(pathname = "") {
  const trimmed = String(pathname || "").trim();
  if (!trimmed || trimmed === "/") return "/";
  return trimmed.replace(/\/+$/, "");
}

export function getTutorPathForTab(tabKey) {
  return TAB_PATHS[tabKey] || null;
}

export function getTutorTabFromPath(pathname = "") {
  const p = normalizePathname(pathname);

  if (p === "/") return TUTOR_DEFAULT_TAB;
  if (p === "/announcements") return "announcements";
  if (p === "/suggestions") return "suggestions";
  if (p === "/schedule") return "schedule";
  if (p === "/availability") return "availability";
  if (p === "/payroll") return "payroll";

  if (p === "/session" || p === "/session/log") return "session__log";
  if (p === "/session/progress-report") return "session__progress";

  if (p === "/curriculum" || p.startsWith("/curriculum/")) return "curriculum";

  if (p === "/students" || p === "/students/directory" || p.startsWith("/students/directory/")) return "student__directory";
  if (p === "/students/diagnostics") return "student__diagnostics";
  if (p === "/students/plans") return "student__plans";

  if (p === "/my-info" || p === "/my-info/profile") return "myinfo__profile";
  if (p === "/my-info/subjects") return "myinfo__subjects";

  return null;
}

// Resolve the canonical path for the current location. Bare group paths and
// "/" map to a sensible default child; recognized paths normalize to their
// canonical form; unknown paths return null (caller falls back to default).
export function getCanonicalTutorPath(pathname = "", activeTab = TUTOR_DEFAULT_TAB) {
  const p = normalizePathname(pathname);

  if (p === "/") return getTutorPathForTab(activeTab) || TAB_PATHS[TUTOR_DEFAULT_TAB];
  if (p === "/session") return TAB_PATHS.session__log;
  // Curriculum owns its own sub-routing; preserve the full path verbatim so deep
  // links like /curriculum/ap/<textbookId> survive the canonical redirect.
  if (p === "/curriculum" || p.startsWith("/curriculum/")) return p;
  if (p === "/students") return TAB_PATHS.student__directory;
  if (p === "/my-info") return TAB_PATHS.myinfo__profile;

  // Identifier-carrying paths (student detail) are canonical as-is.
  if (PRESERVED_PATH_PREFIXES.some((prefix) => p.startsWith(prefix))) return p;

  const tabKey = getTutorTabFromPath(p);
  if (!tabKey) return null;
  return getTutorPathForTab(tabKey);
}

export function getGroupKeyForTutorTab(tabKey = "") {
  if (tabKey.startsWith("session__")) return "session";
  if (tabKey.startsWith("student__")) return "student";
  if (tabKey.startsWith("myinfo__")) return "myinfo";
  return null;
}

export function shouldPreserveTutorSearch(pathname = "") {
  const p = normalizePathname(pathname);
  return (
    PRESERVE_SEARCH_PATHS.has(p) || PRESERVE_SEARCH_PREFIXES.some((prefix) => p.startsWith(prefix))
  );
}
